import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Upload, 
  Image as ImageIcon, 
  User, 
  Package, 
  Palette,
  X,
  CheckCircle2,
  Camera,
  Sparkles,
  Grid3X3,
  Circle,
  Square,
  Hexagon
} from 'lucide-react';
import { toast } from 'sonner';

// Storage key for brand assets
const BRAND_ASSETS_KEY = 'launch_ai_brand_assets';

// Icon style options
const ICON_STYLES = [
  { id: 'linear', label: 'Lineal', icon: Circle, description: 'Iconos con líneas finas y elegantes' },
  { id: 'filled', label: 'Relleno', icon: Square, description: 'Iconos sólidos con colores vivos' },
  { id: 'isometric', label: 'Isométrico', icon: Hexagon, description: 'Iconos 3D con perspectiva' },
  { id: 'gradient', label: 'Gradiente', icon: Sparkles, description: 'Iconos con degradados modernos' }
];

// Image uploader component
const ImageUploader = ({ 
  label, 
  description, 
  value, 
  onChange, 
  placeholderIcon: PlaceholderIcon,
  aspectRatio = '1/1'
}) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, sube un archivo de imagen');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      onChange(reader.result);
      toast.success(`${label} subida correctamente`);
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleRemove = () => {
    onChange(null);
    toast.success('Imagen eliminada');
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-text-main">{label}</label>
      <p className="text-xs text-text-muted">{description}</p>
      
      <div
        className={`relative border-2 border-dashed rounded-xl transition-all overflow-hidden ${
          isDragging 
            ? 'border-brand-500 bg-brand/10' 
            : value 
            ? 'border-[#00D68F] bg-[#00D68F]/5' 
            : 'border-border-subtle hover:border-brand/50'
        }`}
        style={{ aspectRatio }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {value ? (
          <>
            <img 
              src={value} 
              alt={label}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => inputRef.current?.click()}
                className="bg-white/20 backdrop-blur-sm text-white hover:bg-white/30"
              >
                <Camera className="w-4 h-4 mr-1" />
                Cambiar
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={handleRemove}
                className="bg-red-500/50 backdrop-blur-sm text-white hover:bg-red-500/70"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="absolute top-2 right-2">
              <div className="w-6 h-6 rounded-full bg-[#00D68F] flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-white" />
              </div>
            </div>
          </>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-full h-full flex flex-col items-center justify-center gap-3 p-4 min-h-[150px]"
          >
            <div className="w-16 h-16 rounded-full bg-app-bg flex items-center justify-center">
              {PlaceholderIcon ? (
                <PlaceholderIcon className="w-8 h-8 text-text-muted" />
              ) : (
                <Upload className="w-8 h-8 text-text-muted" />
              )}
            </div>
            <div className="text-center">
              <p className="text-sm text-text-secondary">
                Arrastra una imagen o <span className="text-brand-500">haz clic para subir</span>
              </p>
              <p className="text-xs text-text-muted mt-1">PNG, JPG hasta 5MB</p>
            </div>
          </button>
        )}
        
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>
    </div>
  );
};

// Main BrandAssets component
const BrandAssets = ({ assets, onAssetsChange, isExpanded = false }) => {
  const [expanded, setExpanded] = useState(isExpanded);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(BRAND_ASSETS_KEY);
    if (saved && !assets?.expertPhoto && !assets?.productPhoto) {
      try {
        const parsed = JSON.parse(saved);
        onAssetsChange(parsed);
      } catch (e) {
        
      }
    }
  }, []);

  // Save to localStorage when assets change
  useEffect(() => {
    if (assets && (assets.expertPhoto || assets.productPhoto || assets.iconStyle)) {
      localStorage.setItem(BRAND_ASSETS_KEY, JSON.stringify(assets));
    }
  }, [assets]);

  const updateAsset = (key, value) => {
    onAssetsChange({ ...assets, [key]: value });
  };

  return (
    <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-app-bg/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
            <Palette className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="font-medium text-text-main">Galería de Activos del Proyecto</h3>
            <p className="text-xs text-text-muted">Sube fotos y elige estilo de iconos para tus landings</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {(assets?.expertPhoto || assets?.productPhoto) && (
            <span className="text-xs bg-[#00D68F]/10 text-[#00D68F] px-2 py-1 rounded-full">
              {[assets?.expertPhoto, assets?.productPhoto].filter(Boolean).length} imagen(es)
            </span>
          )}
          <svg
            className={`w-5 h-5 text-text-muted transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      
      {expanded && (
        <div className="p-4 pt-0 space-y-6 border-t border-border-subtle">
          {/* Image Uploaders */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
            <ImageUploader
              label="Foto de Autoridad/Experto"
              description="Foto principal del profesional (idealmente con fondo transparente)"
              value={assets?.expertPhoto}
              onChange={(value) => updateAsset('expertPhoto', value)}
              placeholderIcon={User}
              aspectRatio="3/4"
            />
            
            <ImageUploader
              label="Imagen de Contexto/Producto"
              description="Foto del infoproducto o servicio en acción"
              value={assets?.productPhoto}
              onChange={(value) => updateAsset('productPhoto', value)}
              placeholderIcon={Package}
              aspectRatio="16/9"
            />
          </div>
          
          {/* Icon Style Selector */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-text-main">Pack de Iconos</label>
            <p className="text-xs text-text-muted">Elige el estilo visual para los iconos de tus landings</p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {ICON_STYLES.map((style) => {
                const IconComponent = style.icon;
                const isSelected = assets?.iconStyle === style.id;
                
                return (
                  <button
                    key={style.id}
                    type="button"
                    onClick={() => updateAsset('iconStyle', style.id)}
                    className={`p-4 rounded-xl border-2 transition-all text-center ${
                      isSelected
                        ? 'border-brand-500 bg-brand/10'
                        : 'border-border-subtle hover:border-brand/30'
                    }`}
                  >
                    <div className={`w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-2 ${
                      isSelected ? 'bg-brand/20' : 'bg-app-bg'
                    }`}>
                      <IconComponent className={`w-6 h-6 ${isSelected ? 'text-brand' : 'text-text-muted'}`} />
                    </div>
                    <p className={`text-sm font-medium ${isSelected ? 'text-brand' : 'text-text-main'}`}>
                      {style.label}
                    </p>
                    <p className="text-xs text-text-muted mt-1 line-clamp-2">
                      {style.description}
                    </p>
                    {isSelected && (
                      <div className="mt-2">
                        <CheckCircle2 className="w-4 h-4 text-brand-500 mx-auto" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Preview hint */}
          <div className="bg-app-bg/50 rounded-lg p-4 flex items-start gap-3">
            <ImageIcon className="w-5 h-5 text-brand-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-text-main font-medium">Dónde aparecerán tus imágenes</p>
              <ul className="text-xs text-text-muted mt-2 space-y-1">
                <li>• <strong>Foto de Autoridad:</strong> Sección Hero y Sobre el Experto</li>
                <li>• <strong>Imagen de Producto:</strong> Sección de Oferta y Beneficios</li>
                <li>• <strong>Estilo de Iconos:</strong> Lista de características y puntos clave</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandAssets;
export { ICON_STYLES, BRAND_ASSETS_KEY };
